import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import {
  type CustomerFieldBindingConfidenceHint,
  type CustomerFieldBindingOverwritePolicy,
  type CustomerFieldBindingTransform,
  type CustomerFieldMappingTarget,
  customerFieldTargetOptions,
} from '@/contracts/customer-application-binding'
import type { CustomerFieldMappingContract } from '@/contracts/customers'
import {
  defaultCustomerFieldTransform,
  getCustomerFieldMappingConflictAdvice,
  getCustomerFieldTargetLabel,
  getCustomerFieldTargetOption,
  summarizeQuestionOptionRules,
  summarizeQuestionValidationRules,
} from '@/lib/customer-field-mapping-ui'
import { cn } from '@/lib/utils'
import { useBusinessWorkflowStore } from '@/stores/businessWorkflowStore'
import type { Question } from '@/types/form'
import { isLayoutQuestionType } from '@/types/form'
import { Info, Link2, TriangleAlert } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { getQuestionTypeZhName } from '../question-type-meta'

type CustomerFieldBindingSettingsProps = {
  question: Question
  disabled?: boolean
}

const noBindingValue = '__none__'

const transformOptions: Array<{ value: CustomerFieldBindingTransform; label: string }> = [
  { value: 'none', label: '不轉換' },
  { value: 'trim', label: '去除前後空白' },
  { value: 'number', label: '轉為數字' },
  { value: 'currency', label: '轉為金額' },
  { value: 'phone', label: '電話格式' },
  { value: 'email', label: 'Email 小寫化' },
  { value: 'url', label: '網址格式' },
  { value: 'date', label: '日期格式' },
  { value: 'join_choices', label: '多選合併文字' },
]

const overwritePolicyOptions: Array<{
  value: CustomerFieldBindingOverwritePolicy
  label: string
  description: string
}> = [
  {
    value: 'prefill_if_empty',
    label: '空值才帶入',
    description: '申請表欄位已有人工值時保留人工值。',
  },
  {
    value: 'overwrite_manual',
    label: '覆寫手動值',
    description: '重新由問卷答案覆寫欄位，需在送出前再次確認。',
  },
  {
    value: 'ask_before_overwrite',
    label: '覆寫前詢問',
    description: '偵測已有人工值時顯示確認提示。',
  },
]

const confidenceOptions: Array<{
  value: CustomerFieldBindingConfidenceHint
  label: string
  description: string
}> = [
  { value: 'exact', label: '精準', description: '題目語意與欄位完全一致。' },
  { value: 'likely', label: '可能', description: '大致可對應，但建議審核時確認。' },
  { value: 'manual_review', label: '需人工確認', description: '需要主管或業務人工判斷。' },
]

function buildMapping(
  question: Question,
  targetField: CustomerFieldMappingTarget,
  previous?: CustomerFieldMappingContract,
): CustomerFieldMappingContract {
  const option = getCustomerFieldTargetOption(targetField)
  const specQuestionNo = Number(question.settings.specQuestionNo)
  return {
    id: previous?.id ?? `map-${question.id}`,
    formId: question.formId,
    questionId: question.id,
    questionTitle: question.title,
    targetField,
    targetLabel: option?.shortLabel ?? option?.label ?? targetField,
    dbFieldName: option?.dbFieldName,
    dataType: option?.dataType,
    specQuestionNo: Number.isFinite(specQuestionNo) ? specQuestionNo : undefined,
    choiceRuleSummary: summarizeQuestionOptionRules(question),
    validationRuleSummary: summarizeQuestionValidationRules(question),
    transform: previous?.transform ?? defaultCustomerFieldTransform(question.type, targetField),
    requiredForApproval: previous?.requiredForApproval ?? true,
    overwritePolicy: previous?.overwritePolicy ?? 'prefill_if_empty',
    confidenceHint: previous?.confidenceHint ?? 'exact',
    updatedAt: new Date().toISOString(),
  }
}

export function CustomerFieldBindingSettings({
  question,
  disabled = false,
}: CustomerFieldBindingSettingsProps) {
  const fieldMappings = useBusinessWorkflowStore((state) => state.fieldMappings)
  const updateFieldMappings = useBusinessWorkflowStore((state) => state.updateFieldMappings)
  const formMappings = useMemo(
    () => fieldMappings.filter((mapping) => mapping.formId === question.formId),
    [fieldMappings, question.formId],
  )
  const currentMapping = formMappings.find((mapping) => mapping.questionId === question.id)
  const targetConflicts = currentMapping
    ? formMappings.filter(
        (mapping) =>
          mapping.targetField === currentMapping.targetField && mapping.questionId !== question.id,
      )
    : []
  const isLayout = isLayoutQuestionType(question.type)

  useEffect(() => {
    if (!currentMapping || disabled || currentMapping.questionTitle === question.title) return
    updateMapping({ questionTitle: question.title, updatedAt: new Date().toISOString() })
  }, [currentMapping, disabled, question.title])

  function saveMappings(nextMappings: CustomerFieldMappingContract[]) {
    updateFieldMappings(question.formId, nextMappings)
  }

  function setTarget(targetField: CustomerFieldMappingTarget | typeof noBindingValue) {
    if (disabled || isLayout) return
    if (targetField === noBindingValue) {
      saveMappings(formMappings.filter((mapping) => mapping.questionId !== question.id))
      return
    }

    const nextMapping = buildMapping(question, targetField, currentMapping)
    const withoutCurrent = formMappings.filter((mapping) => mapping.questionId !== question.id)
    saveMappings([...withoutCurrent, nextMapping])
  }

  function updateMapping(patch: Partial<CustomerFieldMappingContract>) {
    if (!currentMapping || disabled || isLayout) return
    saveMappings(
      formMappings.map((mapping) =>
        mapping.id === currentMapping.id
          ? {
              ...mapping,
              ...patch,
              questionId: question.id,
              questionTitle: question.title,
              updatedAt: new Date().toISOString(),
            }
          : mapping,
      ),
    )
  }

  return (
    <section className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-slate-950">新客申請欄位</h3>
            <Badge variant="outline">mock-safe</Badge>
          </div>
          <p className="mt-1 text-xs leading-5 text-slate-500">
            指定這題答案要自動帶入申請表哪個欄位；目前只寫入 persistent mock store。
          </p>
        </div>
        <Link2 className="mt-0.5 size-4 shrink-0 text-indigo-500" />
      </div>

      {isLayout ? (
        <div className="mt-3 rounded-md border border-slate-200 bg-white p-3 text-xs leading-5 text-slate-500">
          {getQuestionTypeZhName(question.type)}屬於版面區塊，不會產生可帶入新客申請的答案。 欄位
          binding 只適用於會產生回答值的問題，例如文字、選項、數字、日期與檔案等題型。
        </div>
      ) : (
        <div className={cn('mt-4 grid gap-4', disabled && 'pointer-events-none opacity-60')}>
          <div className="space-y-2">
            <Label>對應申請欄位</Label>
            <Select
              value={currentMapping?.targetField ?? noBindingValue}
              disabled={disabled}
              onValueChange={(value) =>
                setTarget(value as CustomerFieldMappingTarget | typeof noBindingValue)
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value={noBindingValue}>不指定</SelectItem>
                {customerFieldTargetOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {currentMapping ? (
            <div className="grid gap-4">
              {targetConflicts.length > 0 ? (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-900">
                  <div className="flex items-center gap-2 font-medium">
                    <TriangleAlert className="size-4" />
                    {getCustomerFieldTargetLabel(currentMapping.targetField, 'short')}
                    已有其他題目對應
                  </div>
                  <p className="mt-1">
                    {getCustomerFieldMappingConflictAdvice(currentMapping.targetField, [
                      currentMapping,
                      ...targetConflicts,
                    ])}
                  </p>
                </div>
              ) : null}

              <div className="rounded-md border border-white bg-white/80 p-3 text-xs leading-5 text-slate-600">
                <div className="font-medium text-slate-900">DB 欄位綁定</div>
                <div className="mt-1">
                  建議 DB 欄位名：
                  <span className="font-mono text-slate-950">
                    {currentMapping.dbFieldName ??
                      getCustomerFieldTargetOption(currentMapping.targetField)?.dbFieldName ??
                      '未指定'}
                  </span>
                </div>
                <div className="mt-1">
                  資料型態：
                  <span className="font-mono text-slate-950">
                    {currentMapping.dataType ??
                      getCustomerFieldTargetOption(currentMapping.targetField)?.dataType ??
                      '未指定'}
                  </span>
                </div>
              </div>

              <div className="grid gap-3">
                <div className="space-y-2">
                  <Label>資料轉換方式</Label>
                  <Select
                    value={
                      currentMapping.transform ??
                      defaultCustomerFieldTransform(question.type, currentMapping.targetField)
                    }
                    disabled={disabled}
                    onValueChange={(transform) =>
                      updateMapping({ transform: transform as CustomerFieldBindingTransform })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {transformOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between gap-3 rounded-md border border-white bg-white/80 p-3">
                  <div>
                    <Label className="text-sm">主管審核前必填</Label>
                    <p className="mt-1 text-xs text-slate-500">
                      若來源答案缺失，申請送審前會提示補齊。
                    </p>
                  </div>
                  <Switch
                    checked={currentMapping.requiredForApproval}
                    disabled={disabled}
                    onCheckedChange={(requiredForApproval) =>
                      updateMapping({ requiredForApproval })
                    }
                    aria-label="主管審核前必填"
                  />
                </div>

                <div className="space-y-2">
                  <Label>覆寫策略</Label>
                  <Select
                    value={currentMapping.overwritePolicy ?? 'prefill_if_empty'}
                    disabled={disabled}
                    onValueChange={(overwritePolicy) =>
                      updateMapping({
                        overwritePolicy: overwritePolicy as CustomerFieldBindingOverwritePolicy,
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {overwritePolicyOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs leading-5 text-slate-500">
                    {
                      overwritePolicyOptions.find(
                        (option) => option.value === currentMapping.overwritePolicy,
                      )?.description
                    }
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>信心提示</Label>
                  <Select
                    value={currentMapping.confidenceHint ?? 'exact'}
                    disabled={disabled}
                    onValueChange={(confidenceHint) =>
                      updateMapping({
                        confidenceHint: confidenceHint as CustomerFieldBindingConfidenceHint,
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {confidenceOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs leading-5 text-slate-500">
                    {
                      confidenceOptions.find(
                        (option) => option.value === currentMapping.confidenceHint,
                      )?.description
                    }
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex gap-2 rounded-md border border-slate-200 bg-white p-3 text-xs leading-5 text-slate-500">
              <Info className="mt-0.5 size-4 shrink-0 text-slate-400" />
              不指定時，這題答案只保留在問卷回應，不會自動帶入新客申請表。
            </div>
          )}
        </div>
      )}
    </section>
  )
}
